import { Controller } from '@hotwired/stimulus'

/**
 * RequiredFieldsSubmitController
 *
 * Automatically disables the form's submit button until all required fields are completed.
 * Works seamlessly with TailwindFormBuilder and core input components.
 *
 * Usage:
 *   <form data-controller="required-fields-submit">
 *     <input type="text" name="name" required>
 *     <button type="submit">Save</button>
 *   </form>
 *
 * Note: This controller does NOT support dynamically added fields (e.g., via nested forms).
 * Required fields are detected once on connect. If you need dynamic field support,
 * consider extending this controller with a MutationObserver.
 */
export default class extends Controller {
  static targets = ['submit']

  connect() {
    this.bindEvents()
    this.checkValidity()
  }

  disconnect() {
    this.unbindEvents()
  }

  bindEvents() {
    this.boundCheckValidity = this.checkValidity.bind(this)

    this.fieldsToWatch.forEach(field => {
      field.addEventListener('input', this.boundCheckValidity)
      field.addEventListener('change', this.boundCheckValidity)
    })
  }

  unbindEvents() {
    this.fieldsToWatch.forEach(field => {
      field.removeEventListener('input', this.boundCheckValidity)
      field.removeEventListener('change', this.boundCheckValidity)
    })
  }

  checkValidity() {
    const allValid = this.requiredFields.every(field => this.isFieldValid(field))
    if (this.submitButton) {
      this.submitButton.disabled = !allValid
    }
  }

  isFieldValid(field) {
    const tagName = field.tagName.toLowerCase()
    const type = field.type?.toLowerCase()

    // Checkbox: must be checked
    if (type === 'checkbox') {
      return field.checked
    }

    // Radio: at least one in the group must be checked
    if (type === 'radio') {
      const name = field.name
      const group = this.element.querySelectorAll(`input[type="radio"][name="${name}"]`)
      return Array.from(group).some(radio => radio.checked)
    }

    // Select: value must not be empty (prompt options have empty value)
    if (tagName === 'select') {
      return field.value !== ''
    }

    // Text-like inputs and textarea: must have non-empty trimmed value
    return field.value.trim() !== ''
  }

  get requiredFields() {
    return Array.from(this.element.querySelectorAll('[required]'))
  }

  // Returns all fields we need to watch for changes.
  // For radio buttons, we need to watch ALL radios in a group, not just the required one,
  // because the change event fires on whichever radio was clicked.
  get fieldsToWatch() {
    const fields = new Set(this.requiredFields)

    // For each required radio, add all other radios in the same group
    this.requiredFields
      .filter(field => field.type?.toLowerCase() === 'radio')
      .forEach(radio => {
        const groupRadios = this.element.querySelectorAll(`input[type="radio"][name="${radio.name}"]`)
        groupRadios.forEach(r => fields.add(r))
      })

    return Array.from(fields)
  }

  get submitButton() {
    if (this.hasSubmitTarget) {
      return this.submitTarget
    }

    // Button inside the form
    const internalButton = this.element.querySelector('[type="submit"]')
    if (internalButton) {
      return internalButton
    }

    // Button outside the form, linked via HTML form attribute
    const formId = this.element.id
    if (formId) {
      return document.querySelector(`[type="submit"][form="${formId}"]`)
    }

    return null
  }
}
